import { useLocation, useParams } from "react-router-dom";
import { ISSUE_LABEL, RANGE_LABEL } from "@k8s-dashboard/contracts";
import { drillKeys, usePodDetail } from "@/api/queries";
import { useDashboardParams } from "@/state/useDashboardParams";
import { Panel, StatTile, StatusBadge } from "@/components/primitives";
import { SectionView } from "@/components/SectionState";
import { Breadcrumb, LogsLink, OwnerChain, TrendsGrid, UsageBar, drillCrumbs } from "@/components/drill";
import { EventFeed } from "@/features/overview/panels";
import { num, since } from "@/lib/format";
import { PageError, PageHeader, useDrillControls, useInvalidate } from "./common";
import { ContainerTable, PodManagedCards, ownerDeploymentName } from "./PodDetailCards";
import { DebugTerminal } from "@/features/debug/DebugTerminal";
import { LiveLogs } from "./LiveLogs";
import { AgentEntry } from "@/features/agent/AgentEntry";
import { PodStructureCard } from "./PodStructureCard";
import { PodContainerMetricsCard } from "./PodContainerMetricsCard";

/**
 * Pod 상세 — 이슈 #15
 * Container phase · waiting reason · 배포 이미지 · Owner 체인 · 로그 연결.
 *
 * URL의 `uid`가 신원입니다. 이름이 같아도 재생성된 Pod는 다른 인스턴스이며,
 * 캐시 키와 화면 표시가 모두 UID를 따릅니다. (완료 기준)
 */
export function PodDetail() {
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  /* namespace와 이름은 경로가, 신원(uid)은 쿼리가 정합니다. (#111) */
  const { namespace = "", name = "" } = useParams();
  const ns = namespace;
  const uid = params.get("uid") ?? "";
  const { clusterId, range, from, to, refreshMs } = useDashboardParams();
  const q = usePodDetail(clusterId, ns, name, uid, range, refreshMs, from, to);
  const invalidate = useInvalidate(drillKeys.pod(clusterId, ns, name, uid, range));
  const { controls } = useDrillControls(invalidate, q.isFetching, q.dataUpdatedAt || undefined);

  const pod = q.data?.pod.data;
  const ref = q.data?.generatedAt;
  const owner = pod?.owner;

  return (
    <div className="page">
      <PageHeader
        crumbs={
          <Breadcrumb
            items={drillCrumbs(search, ns, ...(owner ? [{ label: owner.name }] : []), { label: name })}
          />
        }
        title={
          <span className="row">
            {name}
            {pod && (
              /* CrashLoopBackOff Pod의 phase는 실제로 Running입니다. phase만 보여주면
                 "왜 Running인데 빨간색이지?"가 되므로 컨테이너 사유를 함께 붙입니다. */
              <StatusBadge
                severity={pod.severity}
                label={pod.issues.length ? `${pod.phase} · ${ISSUE_LABEL[pod.issues[0]!]}` : pod.phase}
                small
              />
            )}
            {pod?.finishedAt && <span className="muted">종료된 인스턴스</span>}
          </span>
        }
        subtitle={
          <>
            {ns} · {clusterId} · {RANGE_LABEL[range]}
            {pod && (
              <>
                {" · "}
                <span className="ds-ident">UID {pod.uid}</span>
              </>
            )}
          </>
        }
        controls={controls}
        actions={
          pod ? (
            <div className="row row--wrap">
              <LogsLink resourceRef={pod.ref} search={search}>이 Pod의 로그 열기 →</LogsLink>
              <span className="muted">동일 시간 범위가 그대로 전달됩니다</span>
              {/* AI Agent(ADR 0026)는 **응답에서 읽은 UID**를 넘깁니다. URL의 이름이
                  아닙니다 — 이름이 같아도 재생성된 Pod는 다른 인스턴스입니다. */}
              <AgentEntry
                clusterId={clusterId}
                namespace={pod.namespace}
                range={range}
                podUid={pod.uid}
                workloadUid={pod.ref.workloadUid}
                subjectName={pod.name}
                subjectKind="Pod"
              />
            </div>
          ) : undefined
        }
      />

      {q.isError ? (
        <PageError error={q.error} onRetry={invalidate} />
      ) : (
        <>
          {pod && (
            <div className="grid grid--kpi">
              <StatTile
                label="Phase"
                value={pod.phase}
                tone={pod.severity === "critical" ? "critical" : pod.severity === "warning" ? "warning" : undefined}
                footnote={
                  pod.issues.length
                    ? `컨테이너 사유: ${pod.issues.map((i) => ISSUE_LABEL[i]).join(" · ")}`
                    : "모든 컨테이너 정상"
                }
              />
              <StatTile label="Ready" value={pod.ready} footnote={`재시작 ${pod.restarts}회`} />
              <StatTile
                label="CPU / Request"
                value={`${(pod.usage.cpuVsRequest * 100).toFixed(0)}%`}
                footnote={`${num(pod.usage.cpuMilli)}m / ${num(pod.usage.cpuRequestMilli)}m`}
              />
              <StatTile
                label="Memory / Request"
                value={`${(pod.usage.memoryVsRequest * 100).toFixed(0)}%`}
                footnote={`${num(pod.usage.memoryMib)}MiB / ${num(pod.usage.memoryRequestMib)}MiB`}
              />
              <StatTile
                label="Node"
                value={<span className="ds-ident">{pod.node}</span>}
                footnote={
                  pod.finishedAt
                    ? `${since(pod.startedAt, ref!)} 시작 · ${since(pod.finishedAt, ref!)} 종료`
                    : `${since(pod.startedAt, ref!)} 시작`
                }
              />
            </div>
          )}

          <div className="grid grid--split">
            <Panel title="Container" section={q.data?.containers} referenceIso={ref} flush>
              <SectionView section={q.data?.containers} loading={q.isLoading} emptyTitle="Container 정보가 없습니다">
                {(containers) => <ContainerTable containers={containers} />}
              </SectionView>
            </Panel>

            <div className="grid">
              <Panel title="OwnerReference" subtitle="상위 소유 체인" section={q.data?.ownerChain} referenceIso={ref}>
                <SectionView section={q.data?.ownerChain} loading={q.isLoading} emptyTitle="소유자가 없습니다">
                  {(chain) => <OwnerChain chain={chain} />}
                </SectionView>
              </Panel>

              {pod && (
                <Panel title="식별 정보" section={q.data?.pod} referenceIso={ref}>
                  <div className="facts">
                    <div className="fact">
                      <span className="fact__label">Pod UID</span>
                      <span className="fact__value ds-ident">{pod.uid}</span>
                    </div>
                    <div className="fact">
                      <span className="fact__label">Namespace</span>
                      <span className="fact__value">{pod.namespace}</span>
                    </div>
                    <div className="fact">
                      <span className="fact__label">문제</span>
                      <span className="fact__value">
                        {pod.issues.length ? pod.issues.map((i) => ISSUE_LABEL[i]).join(" · ") : "없음"}
                      </span>
                    </div>
                    <div className="fact">
                      <span className="fact__label">CPU 사용</span>
                      <span className="fact__value">
                        <UsageBar ratio={pod.usage.cpuVsLimit} label="CPU Limit" />
                      </span>
                    </div>
                  </div>
                </Panel>
              )}
            </div>
          </div>

          <PodStructureCard section={q.data?.structured} referenceIso={ref} />

          <PodContainerMetricsCard section={q.data?.containerMetrics} referenceIso={ref} />

          {pod && (
            <LiveLogs
              clusterId={clusterId}
              namespace={ns}
              podName={name}
              podUid={pod.uid}
              search={search}
              autoOpen={params.get("live") === "1"}
              initialContainer={params.get("container") ?? ""}
            />
          )}

          {pod && (
            <DebugTerminal clusterId={clusterId} namespace={ns} podName={name} podUid={pod.uid} />
          )}

          {pod && (
            <PodManagedCards
              clusterId={clusterId}
              namespace={ns}
              search={search}
              deploymentName={ownerDeploymentName(q.data?.ownerChain.data)}
              secretRefs={q.data?.secretRefs ?? []}
            />
          )}

          <SectionView section={q.data?.trends} loading={q.isLoading} emptyTitle="추세 데이터가 없습니다">
            {(panels) => (
              <TrendsGrid panels={panels} section={q.data?.trends} range={range} referenceIso={ref} labelPrefix={name} />
            )}
          </SectionView>

          <Panel title="최근 Event" section={q.data?.events} referenceIso={ref} flush>
            <SectionView section={q.data?.events} loading={q.isLoading} emptyTitle="관련 이벤트가 없습니다">
              {(events) => <EventFeed events={events} referenceIso={ref!} />}
            </SectionView>
          </Panel>
        </>
      )}
    </div>
  );
}
